import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { getInitials } from "@/lib/initials";
import { PaperCard } from "@/components/PaperCard";
import { Loader2, ChevronRight, Crown, Building2, ArrowLeft, Briefcase, Mail, Globe } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function MemberProfile() {
  const { slug, key } = useParams();
  const [journal, setJournal] = useState(null);
  const [papers, setPapers] = useState(null);

  useEffect(() => {
    api.get(`/journals/${slug}`).then(({ data }) => setJournal(data)).catch(() => setJournal(false));
  }, [slug]);

  const isEic = key === "eic";
  const member = journal
    ? (isEic ? { name: journal.editor_in_chief, role: "Editor-in-Chief", designation: journal.eic_designation, department: journal.eic_department, affiliation: journal.eic_affiliation, email: journal.eic_email, bio: journal.eic_bio, homepage: journal.eic_homepage, photo: journal.eic_photo } : (journal.editorial_board || [])[Number(key)])
    : null;
  const cleanName = member?.name ? member.name.replace(/\s*\(.*\)\s*$/, "").trim() : "";

  useEffect(() => {
    if (cleanName) api.get("/papers/by-author", { params: { name: cleanName } }).then(({ data }) => setPapers(data)).catch(() => setPapers([]));
  }, [cleanName]);

  if (journal === null) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!journal || !member) return <div className="max-w-7xl mx-auto px-6 py-20">Member not found. <Link to="/journals" className="text-primary underline">Browse journals</Link></div>;

  const initials = getInitials(member.name);

  return (
    <div data-testid="member-profile">
      <div className="bg-primary text-white">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-5">
            <Link to="/" className="hover:text-amber-400">Home</Link><ChevronRight className="w-4 h-4" />
            <Link to={`/journal/${slug}`} className="hover:text-amber-400">{journal.title}</Link><ChevronRight className="w-4 h-4" />
            <span>Editorial Board</span>
          </div>
          <div className="flex items-center gap-5">
            {member.photo
              ? <img src={`${BACKEND}${member.photo}`} alt={member.name} className="w-20 h-20 rounded-full object-cover ring-2 ring-white/20" />
              : <span className="grid place-items-center w-20 h-20 rounded-full bg-amber-500 text-white font-bold text-2xl shrink-0">{initials}</span>}
            <div>
              <h1 className="font-serif font-black text-3xl flex items-center gap-2">{member.name}{isEic && <Crown className="w-5 h-5 text-amber-400" />}</h1>
              <p className="text-amber-300 font-medium mt-1">{member.role}</p>
              {[member.designation, member.department].filter(Boolean).length > 0 && (
                <p className="text-slate-300 text-sm mt-1 flex items-center gap-1.5" data-testid="member-designation"><Briefcase className="w-4 h-4" /> {[member.designation, member.department].filter(Boolean).join(", ")}</p>
              )}
              {member.affiliation && <p className="text-slate-300 text-sm mt-1 flex items-center gap-1.5"><Building2 className="w-4 h-4" /> {member.affiliation}</p>}
              {member.email && <a href={`mailto:${member.email}`} className="text-slate-300 hover:text-amber-300 text-sm mt-1 flex items-center gap-1.5 break-all" data-testid="member-email"><Mail className="w-4 h-4" /> {member.email}</a>}
              {member.homepage && <a href={member.homepage} target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-amber-300 text-sm mt-1 flex items-center gap-1.5 break-all" data-testid="member-homepage"><Globe className="w-4 h-4" /> Home page</a>}
              <p className="text-slate-400 text-sm mt-1">{journal.title}</p>
            </div>
          </div>
        </div>
      </div>

      <section className="max-w-5xl mx-auto px-6 py-12">
        <Link to={`/journal/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary mb-6" data-testid="back-to-journal"><ArrowLeft className="w-4 h-4" /> Back to {journal.title}</Link>
        {member.bio && (
          <div className="mb-8 max-w-3xl" data-testid="member-bio">
            <h2 className="font-serif font-bold text-xl mb-2 text-slate-900 dark:text-slate-100">Biography</h2>
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-justify">{member.bio}</p>
          </div>
        )}
        <h2 className="font-serif font-bold text-2xl mb-5 text-slate-900 dark:text-slate-100">Published Articles</h2>
        {papers === null ? (
          <div className="py-12 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
        ) : papers.length === 0 ? (
          <p className="text-slate-500" data-testid="member-no-papers">No published articles are associated with {member.name} yet.</p>
        ) : (
          <div className="grid gap-4" data-testid="member-papers">{papers.map((p) => <PaperCard key={p.id} paper={p} />)}</div>
        )}
      </section>
    </div>
  );
}
